import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { IntakeStatus, RequirementIntake } from 'api';
import { NgTemplateOutlet } from '@angular/common';
import { AutofocusDirective } from 'common/form/autofocus.directive';
import { ButtonDirective } from 'common/button/button.directive';

export interface ApplicabilityModel {
  applicability: boolean | null;
  notApplicableJustification: string | null;
}

export interface ApplicabilityChangeEvent {
  requirementId: number;
  model: ApplicabilityModel;
}

@Component({
  selector: 'comp-applicability-section',
  templateUrl: './applicability-section.component.html',
  styleUrls: ['./applicability-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, NgTemplateOutlet, AutofocusDirective, ButtonDirective],
})
export class ApplicabilitySectionComponent {
  readonly requirement = input.required<RequirementIntake>();
  readonly collapsed = input<boolean>(false);
  readonly isSubmitPending = input<boolean>(false);
  readonly actionMessage = input<string | undefined>(undefined);

  readonly applicabilityChanged = output<ApplicabilityChangeEvent>();
  readonly applicabilityConfirmed = output<ApplicabilityChangeEvent>();

  protected readonly expanded = signal(true);

  protected readonly applicability = signal<boolean | null>(null);
  protected readonly notApplicableJustification = signal<string>('');

  protected readonly applicabilityHydratedForRequirementId = signal<number | undefined>(undefined);
  protected readonly requiresNotApplicableJustification = computed(
    () => this.applicability() === false,
  );
  protected readonly isNotApplicableJustificationMissing = computed(() => {
    if (!this.requiresNotApplicableJustification()) {
      return false;
    }

    return this.notApplicableJustification().trim().length === 0;
  });

  protected readonly isNotApplicableJustificationModified = computed(
    () => this.notApplicableJustification() !== (this.requirement().justification ?? ''),
  );

  protected readonly showConfirmNotApplicable = computed(
    () =>
      this.applicability() === false &&
      this.requiresNotApplicableJustification() &&
      (this.requirement().intakeStatus !== IntakeStatus.NotApplicable ||
        this.isNotApplicableJustificationModified()),
  );

  protected readonly canConfirmNotApplicable = computed(
    () => !this.isNotApplicableJustificationMissing(),
  );

  protected readonly showConfirmApplicable = computed(
    () =>
      this.applicability() === true &&
      this.requirement().intakeStatus === IntakeStatus.NotApplicable,
  );

  protected readonly canConfirmApplicable = computed(
    () =>
      this.applicability() === true &&
      this.requirement().intakeStatus === IntakeStatus.NotApplicable,
  );

  protected readonly isApplicabilityInfoExpanded = signal(false);

  constructor() {
    effect(() => {
      const selectedRequirement = this.requirement();

      if (
        selectedRequirement === undefined ||
        this.applicabilityHydratedForRequirementId() === selectedRequirement.id
      ) {
        return;
      }

      this.applicability.set(selectedRequirement.applicability ?? null);
      this.notApplicableJustification.set(selectedRequirement.justification ?? '');
      this.applicabilityHydratedForRequirementId.set(selectedRequirement.id);
    });

    effect(() => {
      const isCollapsed = this.collapsed();
      this.expanded.set(!isCollapsed);
    });
  }

  protected expand(): void {
    this.expanded.set(true);
  }

  protected onNotApplicableJustificationInput(event: Event): void {
    const target = event.target;

    if (!(target instanceof HTMLTextAreaElement)) {
      return;
    }

    this.notApplicableJustification.set(target.value.trim());
  }

  protected selectApplicable(): void {
    this.applicability.set(true);
  }

  protected selectNotApplicable(): void {
    this.applicability.set(false);
  }

  protected isApplicable(): boolean {
    return this.applicability() === true || this.applicability() === null;
  }

  protected isNotApplicable(): boolean {
    return this.applicability() === false;
  }

  protected confirmNotApplicable(): void {
    const requirement = this.requirement();

    if (requirement.id === undefined || this.isSubmitPending() || !this.canConfirmNotApplicable()) {
      return;
    }

    this.applicabilityConfirmed.emit({
      requirementId: requirement.id,
      model: this.buildApplicabilityModel(),
    });
  }

  protected confirmApplicable(): void {
    const requirement = this.requirement();

    if (
      requirement.id === undefined ||
      this.isSubmitPending() ||
      requirement.intakeStatus !== IntakeStatus.NotApplicable ||
      !this.canConfirmApplicable()
    ) {
      return;
    }

    this.applicabilityConfirmed.emit({
      requirementId: requirement.id,
      model: this.buildApplicabilityModel(),
    });
  }

  private buildApplicabilityModel(): ApplicabilityModel {
    const applicability = this.applicability();

    if (applicability !== false) {
      return {
        applicability,
        notApplicableJustification: null,
      };
    }

    return {
      applicability,
      notApplicableJustification: this.notApplicableJustification(),
    };
  }

  protected toggleApplicabilityInfo(): void {
    this.isApplicabilityInfoExpanded.update((isExpanded) => !isExpanded);
  }
}
